<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="project-dashboard">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb" :items="headerItems">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>

            <img
              v-if="($store.state.enums.UsedProjectLanguages || []).length > 1"
              class="ml-2"
              :src="`/images/flags/${$path('metadata.language', item)}.svg`"
              width="20"
            />
            <ButtonComponent
              tag="router-link"
              :disabled="!$store.getters['Project/hasRole']('manager')"
              :to="{ name: 'project-edit', params: { id, step: 'publishing' } }"
              :label="labels[item.isPublished ? 'published' : 'unpublished']"
              :icon="{ icon: ['fas', 'circle'], class: item.isPublished ? 'text--success' : 'text--error' }"
              icon-placement="left"
              class="project-dashboard__published"
            />
          </div>
        </template>
        <template #center>
          <transition name="fade">
            <DraftAlert v-if="hasActiveDraft" />
          </transition>
        </template>
      </HeaderComponent>

      <div class="project-dashboard__content">
        <Tasks data-field="tasks" :reference="{ onModel: 'Project', ref: id }" :show-create="false" />
        <Deadlines data-field="deadlines" :reference="{ onModel: 'Project', ref: id }" />
        <Statistics :addons="addons" data-field="statistics" />
        <Units :addons="addons" data-field="units" />
        <LastActivity data-field="last-activity" :reference="{ onModel: 'Project', ref: id }" />
      </div>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapi18n } from '@/i18n'
import Bus from '@/bus'
import { mergeDeepRight } from 'ramda'
import { Nest } from '@/components/Flatfinder/utils'

import Units from '@/components/Project/Dashboard/Units'
import Statistics from '@/components/Project/Dashboard/Statistics'

import HeaderComponent from '@/components/Header'
import Deadlines from '@/components/Deadlines/Widget'
import LastActivity from '@/components/LastActivity/Widget'
import Tasks from '@/components/Task/Widget'
import DraftAlert from '@/components/Draft/Alert'

import EditSteps from './Mutate/Steps'

export default {
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      draft: true,
    }
  },
  computed: {
    ...mapState('Project', ['item', 'promises']),
    ...mapi18n('labels', {
      projects: ['project', 2],
      edit: 'edit',
      published: 'published',
      unpublished: 'unpublished',
      loading() {
        return this.$t('loading', { resource: this.$tc('project', 1).toLowerCase() }) + '...'
      },
    }),
    addons() {
      return this.$path('metadata.addons', this.item) || []
    },
    breadcrumb() {
      return [{ title: this.labels.projects, action: { name: 'projects' } }, { title: this.$metaInfo.title }]
    },
    hasActiveDraft() {
      return typeof this.draft === 'string'
    },
    showDraft() {
      return this.$path('item.metadata.draftAvailable') || this.hasActiveDraft
    },
    editSteps() {
      const data = mergeDeepRight(this.item, { ...this.data, userRoles: this.$store.getters })
      return EditSteps(data).filter(e => {
        switch (e.id) {
          case 'shops':
            return (
              this.$store.getters['Project/hasRole'](/^shop/) &&
              !this.$store.state.enums.DisabledFeatures.includes('project:shop')
            )
          case 'sale':
            return !this.$store.state.enums.DisabledFeatures.includes('project:bid')
          default:
            return true
        }
      })
    },
    reference() {
      return {
        on: this.id,
        onModel: 'Project',
      }
    },
    canCreateFlatfinder() {
      return ['project:presentation', 'project:flatfinder'].some(a => this.addons.includes(a))
    },
    flatfinders() {
      return Nest(this.item.flatfinders)
    },

    headerItems() {
      return [
        {
          component: 'DropdownComponent',
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.$t('show'),
                  icon: ['fal', 'ellipsis-v'],
                },
              },
            ],
            dropdown: [
              {
                component: 'ButtonComponent',
                props: {
                  tag: 'router-link',
                  label: this.$tc('lead', 2),
                  icon: ['fal', 'users'],
                  to: {
                    name: 'project-leads',
                    params: { reference: this.id },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole'](['manager', 'salesman']),
                props: {
                  tag: 'router-link',
                  label: this.$tc('openhouse', 2),
                  icon: ['fal', 'sign'],
                  to: {
                    name: 'project-openhouse',
                    params: { id: this.id },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole'](['signingAuthority', 'shop.signingAuthority']),
                props: {
                  tag: 'router-link',
                  label: this.$tc('document', 2),
                  icon: ['fal', 'file-contract'],
                  to: {
                    name: 'project-contracts',
                    params: { id: this.id },
                  },
                },
              },

              {
                component: 'ButtonComponent',
                condition:
                  ['project:complaint'].some(el => this.addons.includes(el)) &&
                  this.$store.getters['Project/hasRole']('complaint.manager', this.item),
                props: {
                  tag: 'router-link',
                  label: this.$tc('complaint', 2),
                  icon: ['far', 'list-ul'],
                  to: {
                    name: 'project-complaints',
                    params: { id: this.id },
                  },
                },
              },

              {
                component: 'Preview',
                props: {
                  item: this.item,
                  usePortal: true,
                },
              },

              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project:show',
                  query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },

              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project-form-submit:show',
                  query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
            ],
          },
        },

        {
          component: 'Actions',
          props: {
            condition: this.$store.getters.hasAccess('admin'),
            placement: 'project',
            query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
            'use-portal': false,
          },
        },

        {
          component: 'DropdownComponent',
          condition: this.$store.getters['Project/hasRole']('manager'),
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.labels.edit,
                  icon: ['fal', 'pen'],
                },
              },
            ],
            dropdown: [
              ...this.editSteps
                .filter(s => {
                  switch (s.id) {
                    case 'shops':
                      return this.$store.getters['Project/hasRole'](/^shop/)
                    case 'sale':
                      return !this.$store.state.enums.DisabledFeatures.includes('project:bid')
                    default:
                      return true
                  }
                })
                .map(i => {
                  return {
                    component: 'ButtonComponent',
                    props: {
                      tag: 'router-link',
                      label: i.label,
                      icon: i.icon,
                      to: {
                        name: 'project-edit',
                        params: { id: this.id, step: i.id },
                      },
                    },
                  }
                }),

              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project:edit',
                  query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project-form:edit',
                  query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
            ],
          },
        },

        {
          component: 'ButtonComponent',
          condition: this.$store.getters['Project/hasRole']('manager'),
          props: {
            tag: 'router-link',
            label: this.$t('analytics'),
            icon: ['fal', 'analytics'],
            to: {
              name: 'project-analytics',
              params: { id: this.id },
            },
          },
        },

        {
          component: 'DropdownComponent',
          condition: this.$store.getters['Project/hasRole'](['manager', 'salesman']),
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.$t('add'),
                  icon: ['fal', 'plus'],
                  theme: 'primary',
                },
              },
            ],
            dropdown: [
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole']('manager'),
                props: {
                  label: this.$tc('unit'),
                  icon: ['fal', 'home'],
                },
                listeners: {
                  click: () => this.createResidential(),
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters['Project/hasRole']('manager') && this.$glob(this.item.type, '!singular'),
                props: {
                  label: this.$tc('unitSelector'),
                  icon: ['fal', 'layer-plus'],

                  tag: 'router-link',
                  to: this.canCreateFlatfinder
                    ? { name: 'project-flatfinder-create', params: { project: this.item.id } }
                    : { name: 'project-edit', params: { id: this.id, step: 'flatfinders' } },
                },
              },

              {
                component: 'ButtonComponent',
                props: {
                  'v-tooltip.right': {
                    content:
                      this.$path('item.metadata.disabledFields.project.openhouses') ||
                      this.$path('item.metadata.disabledFields.unit.openhouses'),
                    boundariesElement: this.$body,
                  },
                  class: {
                    'elder-button--disabled':
                      this.$path('item.metadata.disabledFields.project.openhouses') ||
                      this.$path('item.metadata.disabledFields.unit.openhouses'),
                  },
                  label: this.$tc('openhouse'),
                  icon: ['fal', 'sign'],

                  tag:
                    this.$path('item.metadata.disabledFields.project.openhouses') ||
                    this.$path('item.metadata.disabledFields.unit.openhouses')
                      ? 'div'
                      : 'router-link',
                  to: {
                    name: 'project-openhouse-create',
                    params: { project: this.id },
                    query: { returnTo: this.$route.path },
                  },
                },
              },

              {
                component: 'ButtonComponent',
                props: {
                  label: this.$tc('lead'),
                  icon: ['fal', 'user'],
                  tag: 'router-link',
                  to: {
                    name: 'project-lead-create',
                    params: { reference: this.id },
                    query: { returnTo: this.$route.path },
                  },
                },
              },

              {
                component: 'ButtonComponent',
                condition: this.$store.getters.isAdmin,
                props: {
                  label: this.$tc('integration'),
                  icon: ['fal', 'puzzle-piece'],
                  tag: 'router-link',
                  to: {
                    name: 'project-edit',
                    params: { project: this.id, step: 'integrations' },
                  },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project:add',
                  query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'project-form:add',
                  query: { project: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
            ],
          },
        },
      ]
    },
  },
  methods: {
    ...mapActions('Project', {
      fetch(dispatch) {
        return dispatch('fetch', [{ id: this.id }, { query: 'ProjectDashboard', draft: this.draft }]).then(data => {
          if (!data) return this.$router.push({ name: 'notfound' })
          this.draft = data.draftId
        })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),
    async createResidential() {
      await Bus.$emit('tab:set', 'units')
      Bus.$emit('residentials:create', true)
    },
  },

  created() {
    Bus.$on('project:refetch', () => {
      this.draft = true
      return this.fetch()
    })
  },
  metaInfo() {
    return {
      title: this.item.name || this.labels.loading,
    }
  },
  components: {
    HeaderComponent,
    DraftAlert,
    Deadlines,
    LastActivity,
    Tasks,
    Units,
    Statistics,
  },
}
</script>

<style lang="scss">
.project-dashboard {
  background-color: $background-grey;

  &__published {
    margin-left: 1rem;
    font-size: 0.65em;
  }

  &__flatfinders {
    &--nested {
      .elder-button__label:before {
        content: 'L';
        opacity: 0.2;
        margin-right: 0.5em;
      }
    }
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 480px 600px;
    grid-gap: 2rem;

    @include respond-below('phone') {
      grid-template-columns: 1fr;
    }

    & > * {
      animation: fadeIn 300ms ease;
    }
  }

  .elder-button__default-tag {
    padding-right: var(--vue-elder-button-padding-x, 1.5em);
  }

  label[for='statistics-selector'] {
    margin-left: auto;
  }

  [data-field='units'] {
    grid-column-end: span 2;
    @include respond-below('phone') {
      grid-column-end: initial;
    }
  }

  .tabs {
    &__details {
      border: none !important;
    }

    &__header {
      ul,
      a {
        border: none !important;
      }

      a {
        background-color: rgba(black, 0.025);
      }

      &--active a {
        background-color: White;
      }
    }

    &__tab {
      padding: 0 !important;
    }
  }
}
</style>
